// /admin/license — license-token upload, decoded feature-flag table,
// expiry countdown, contact-sales link when expired (T125).
//
// Sections:
//   1. Current status card — valid / expired / unlicensed state + claims.
//   2. Feature-flag table — all well-known features, enabled badge per row.
//   3. Token upload form — paste area + submit.
//
// Backend: GET+POST /api/v1/admin/license (admin role required).

import { FormEvent, ReactNode, useState } from 'react';
import useSWR from 'swr';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ExternalLink,
  Key,
  Loader2,
  Mail,
  Minus,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Users,
  XCircle,
} from 'lucide-react';
import {
  ApiError,
  LicenseEnforcementRow,
  LicenseSeats,
  LicenseStatus,
  ADMIN_LICENSE_KEY,
  adminLicenseApi,
  fetcher,
} from '../lib/api';
import { PageHeader, Table, TBody, TD, TH, THead, TR } from '../components/ui';

// Well-known features with human labels (mirrors license.Feature constants).
const KNOWN_FEATURES: { id: string; label: string; description: string }[] = [
  { id: 'sso',                  label: 'SSO',                    description: 'OIDC + SAML single sign-on' },
  { id: 'portfolio',            label: 'Bundles',                description: 'Multi-project bundle dashboards' },
  { id: 'siem_export',          label: 'SIEM Export',            description: 'CEF-format streaming audit export' },
  { id: 'custom_rules',         label: 'Custom Rules',           description: 'YAML DSL + custom analyzer rules' },
  { id: 'audit_append_only',    label: 'Append-only Audit',      description: 'RLS-enforced tamper-proof audit log' },
  { id: 'taint_analysis',       label: 'Taint Analysis',         description: 'Intra-procedural taint tracking (SQLi / XSS)' },
  { id: 'multi_node_opensearch', label: 'Multi-node Search',     description: 'OpenSearch cluster with dedicated master/data pods' },
  { id: 'ide_plugin',           label: 'IDE Plugin',             description: 'LSP bridge for VS Code + IntelliJ' },
];

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function daysUntil(unixTs: number): number {
  return Math.ceil((unixTs * 1000 - Date.now()) / (1000 * 60 * 60 * 24));
}

function fmtDate(unixTs: number): string {
  return new Date(unixTs * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

interface LicenseBodyProps {
  readonly data: LicenseStatus;
  readonly onMutate: (updated: LicenseStatus) => void;
}

function LicenseBody({ data, onMutate }: LicenseBodyProps) {
  const showFeatureTable = data.configured && (data.valid || data.expired) && data.features !== undefined;
  return (
    <>
      <StatusCard status={data} />
      <SeatUsageBanner seats={data.seats} />
      {data.configured ? (
        <>
          {showFeatureTable ? (
            <FeatureTable features={data.features!} />
          ) : (
            <CommunityFeatureTable />
          )}
          <TokenUploadForm onUploaded={onMutate} />
        </>
      ) : (
        <LicenseWizard onActivated={onMutate} />
      )}
      <EnforcementTable rows={data.enforcement} />
    </>
  );
}

export function AdminLicense() {
  const { data, error, isLoading, mutate } =
    useSWR<LicenseStatus>(ADMIN_LICENSE_KEY, fetcher, { revalidateOnFocus: false });

  const onMutate = (updated: LicenseStatus) => mutate(updated, false);

  let licenseContent: ReactNode;
  if (isLoading) {
    licenseContent = (
      <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-sm">Loading license status…</span>
      </div>
    );
  } else if (error) {
    licenseContent = <ErrorBanner error={error} />;
  } else if (data) {
    licenseContent = <LicenseBody data={data} onMutate={onMutate} />;
  } else {
    licenseContent = null;
  }

  return (
    <div className="max-w-3xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<Key />}
        title="License"
        description="Start a trial, upload an enterprise license, or contact sales."
      />

      {licenseContent}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LicenseWizard — three-card wizard shown when no license is loaded (T192)
// ---------------------------------------------------------------------------

interface LicenseWizardProps {
  readonly onActivated: (updated: LicenseStatus) => void;
}

function LicenseWizard({ onActivated }: LicenseWizardProps) {
  return (
    <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-3">
      <TrialCard onActivated={onActivated} />
      <EnterpriseUploadCard onActivated={onActivated} />
      <SalesCard />
    </div>
  );
}

function TrialCard({ onActivated }: { readonly onActivated: (s: LicenseStatus) => void }) {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [tos, setTos] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !company.trim() || !tos || saving) return;
    setSaving(true);
    setErr(null);
    try {
      const updated = await adminLicenseApi.startTrial(email.trim(), company.trim(), tos);
      onActivated(updated);
    } catch (e) {
      let msg: string;
      if (e instanceof ApiError) {
        msg = `${e.code}: ${e.message}`;
      } else if (e instanceof Error) {
        msg = e.message;
      } else {
        msg = 'request failed';
      }
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="orb-card p-5 flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-5 w-5 text-accent shrink-0" aria-hidden="true" />
        <h2 className="font-semibold text-fg text-sm">Start 30-day Trial</h2>
      </div>
      <p className="text-xs text-fg-muted mb-4 flex-1">
        Unlock all enterprise features for 30 days — no credit card required.
        One trial per organization.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <input
          type="email"
          placeholder="Work email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="
            w-full px-2.5 py-1.5 rounded-md border border-border
            bg-bg-inset text-fg text-xs placeholder-fg-subtle
            focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
            transition-colors
          "
        />
        <input
          type="text"
          placeholder="Company name"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          required
          className="
            w-full px-2.5 py-1.5 rounded-md border border-border
            bg-bg-inset text-fg text-xs placeholder-fg-subtle
            focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
            transition-colors
          "
        />
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={tos}
            onChange={(e) => setTos(e.target.checked)}
            className="mt-0.5 h-3.5 w-3.5 rounded border border-border accent-accent"
          />
          <span className="text-[11px] text-fg-muted leading-snug">
            I accept the{' '}
            <a
              href="https://orbital-lint.io/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline"
            >
              Terms of Service
            </a>
          </span>
        </label>
        {err && (
          <p role="alert" className="text-[11px] text-danger">{err}</p>
        )}
        <button
          type="submit"
          disabled={!email.trim() || !company.trim() || !tos || saving}
          className="
            mt-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md
            text-xs font-semibold uppercase tracking-[0.08em]
            bg-accent text-accent-fg hover:opacity-90 border border-accent/40
            transition-opacity disabled:opacity-30 disabled:cursor-not-allowed
          "
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
          {saving ? 'Starting…' : 'Start Trial'}
        </button>
      </form>
    </div>
  );
}

function EnterpriseUploadCard({ onActivated }: { readonly onActivated: (s: LicenseStatus) => void }) {
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const t = token.trim();
    if (!t || saving) return;
    setSaving(true);
    setErr(null);
    try {
      const updated = await adminLicenseApi.upload(t);
      onActivated(updated);
    } catch (e) {
      let msg: string;
      if (e instanceof ApiError) {
        msg = `${e.code}: ${e.message}`;
      } else if (e instanceof Error) {
        msg = e.message;
      } else {
        msg = 'request failed';
      }
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="orb-card p-5 flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <Key className="h-5 w-5 text-fg-muted shrink-0" aria-hidden="true" />
        <h2 className="font-semibold text-fg text-sm">Upload Enterprise License</h2>
      </div>
      <p className="text-xs text-fg-muted mb-4 flex-1">
        Paste the license token provided by Aphelion Holding after purchase.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <textarea
          rows={3}
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Paste license token…"
          className="
            w-full px-2.5 py-1.5 rounded-md border border-border
            bg-bg-inset text-fg text-xs font-mono placeholder-fg-subtle
            focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
            transition-colors resize-none
          "
        />
        {err && (
          <p role="alert" className="text-[11px] text-danger">{err}</p>
        )}
        <button
          type="submit"
          disabled={!token.trim() || saving}
          className="
            mt-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md
            text-xs font-semibold uppercase tracking-[0.08em]
            bg-bg-inset text-fg hover:bg-border border border-border
            transition-colors disabled:opacity-30 disabled:cursor-not-allowed
          "
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
          {saving ? 'Verifying…' : 'Apply Token'}
        </button>
      </form>
    </div>
  );
}

function SalesCard() {
  return (
    <div className="orb-card p-5 flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="h-5 w-5 text-fg-muted shrink-0" aria-hidden="true" />
        <h2 className="font-semibold text-fg text-sm">Talk to Sales</h2>
      </div>
      <p className="text-xs text-fg-muted mb-4 flex-1">
        Need a custom contract, volume pricing, or have questions about the
        Enterprise tier? Reach out directly.
      </p>
      <a
        href="mailto:sales@aphelion-holding.com"
        className="
          mt-auto inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md
          text-xs font-semibold uppercase tracking-[0.08em]
          bg-bg-inset text-fg hover:bg-border border border-border
          transition-colors
        "
      >
        <ExternalLink className="h-3 w-3" aria-hidden="true" />
        sales@aphelion-holding.com
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatusCard sub-renderers
// ---------------------------------------------------------------------------

function StatusCardCommunity({ status }: { readonly status: LicenseStatus }) {
  return (
    <div className="orb-card p-6 mb-6 flex items-start gap-4">
      <Shield className="h-8 w-8 text-fg-muted mt-0.5 shrink-0" aria-hidden="true" />
      <div>
        <p className="font-semibold text-fg mb-1">Community tier</p>
        <p className="text-sm text-fg-muted">
          No license token uploaded. All core features are available at no cost.
          {status.days_remaining >= 0
            ? ` Grace period ends in ${status.days_remaining} day${status.days_remaining === 1 ? '' : 's'} —`
            : ''}{' '}
          upload a token below to unlock enterprise features.
        </p>
      </div>
    </div>
  );
}

// StatusCardNoLicenseExpired — T620: the no-token counterpart of
// StatusCardExpired below. Before AccessTier was reported on this endpoint,
// an org past its 14-day grace with no license ever uploaded rendered the
// identical, misleadingly calm StatusCardCommunity card as a healthy
// install — with no indication that RequireLicenseWrite (T382) was already
// returning 402 on every write.
function StatusCardNoLicenseExpired() {
  return (
    <div className="orb-card p-6 mb-6 border-danger/40 bg-danger/5 flex items-start gap-4">
      <ShieldAlert className="h-8 w-8 text-danger mt-0.5 shrink-0" aria-hidden="true" />
      <div className="flex-1">
        <p className="font-semibold text-fg mb-1">Read-only — no license</p>
        <p className="text-sm text-fg-muted">
          The 14-day grace period has ended and no license token has been
          uploaded. Write operations, including new scans, are blocked until
          you start a trial or upload an Enterprise license below.
        </p>
      </div>
    </div>
  );
}

function StatusCardExpired({ status }: { readonly status: LicenseStatus }) {
  const expiredOn = status.expires_at ? fmtDate(status.expires_at) : 'unknown date';
  return (
    <div className="orb-card p-6 mb-6 border-warning/40 bg-warning/5 flex items-start gap-4">
      <ShieldAlert className="h-8 w-8 text-warning mt-0.5 shrink-0" aria-hidden="true" />
      <div className="flex-1">
        <p className="font-semibold text-fg mb-1">License expired</p>
        <p className="text-sm text-fg-muted mb-3">
          Your <span className="font-medium">{status.tier}</span> license for{' '}
          <span className="font-medium">{status.licensee}</span> expired on{' '}
          <span className="font-medium">{expiredOn}</span>.
          Enterprise features are now disabled.
        </p>
        <a
          href="https://orbital-lint.io/contact-sales"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
            text-xs font-semibold uppercase tracking-[0.08em]
            bg-warning text-warning-fg hover:opacity-90 transition-opacity"
        >
          Contact sales to renew
        </a>
      </div>
    </div>
  );
}

function StatusCardActiveExpiring({ status, days }: { readonly status: LicenseStatus; readonly days: number }) {
  return (
    <div className="orb-card p-6 mb-6 flex items-start gap-4 border-warning/40 bg-warning/5">
      <ShieldCheck className="h-8 w-8 mt-0.5 shrink-0 text-warning" aria-hidden="true" />
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <p className="font-semibold text-fg">License active</p>
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-warning/20 text-warning">
            {status.tier}
          </span>
        </div>
        <p className="text-sm text-fg-muted">
          Licensed to <span className="font-medium text-fg">{status.licensee}</span>.{' '}
          <span className="text-warning font-medium">
            Expires in {days} day{days === 1 ? '' : 's'} ({fmtDate(status.expires_at!)}).
          </span>
        </p>
        <a
          href="https://orbital-lint.io/contact-sales"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-md
            text-xs font-semibold uppercase tracking-[0.08em]
            bg-warning text-warning-fg hover:opacity-90 transition-opacity"
        >
          Renew license
        </a>
      </div>
    </div>
  );
}

function StatusCardActiveWithExpiry({ status, days }: { readonly status: LicenseStatus; readonly days: number }) {
  return (
    <div className="orb-card p-6 mb-6 flex items-start gap-4">
      <ShieldCheck className="h-8 w-8 mt-0.5 shrink-0 text-success" aria-hidden="true" />
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <p className="font-semibold text-fg">License active</p>
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-success/20 text-success">
            {status.tier}
          </span>
        </div>
        <p className="text-sm text-fg-muted">
          Licensed to <span className="font-medium text-fg">{status.licensee}</span>.{' '}
          <span>Expires {fmtDate(status.expires_at!)} ({days} days).</span>
        </p>
      </div>
    </div>
  );
}

function StatusCardActiveNoExpiry({ status }: { readonly status: LicenseStatus }) {
  return (
    <div className="orb-card p-6 mb-6 flex items-start gap-4">
      <ShieldCheck className="h-8 w-8 text-success mt-0.5 shrink-0" aria-hidden="true" />
      <div>
        <div className="flex items-center gap-2 mb-1">
          <p className="font-semibold text-fg">License active</p>
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-success/20 text-success">
            {status.tier}
          </span>
        </div>
        <p className="text-sm text-fg-muted">
          Licensed to <span className="font-medium text-fg">{status.licensee}</span>. No expiry date.
        </p>
      </div>
    </div>
  );
}

function StatusCardInvalid({ status }: { readonly status: LicenseStatus }) {
  return (
    <div className="orb-card p-6 mb-6 border-danger/40 bg-danger/5 flex items-start gap-4">
      <AlertTriangle className="h-8 w-8 text-danger mt-0.5 shrink-0" aria-hidden="true" />
      <div>
        <p className="font-semibold text-fg mb-1">Invalid license token</p>
        <p className="text-sm text-fg-muted">
          The stored token could not be verified: <span className="text-danger">{status.error}</span>
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatusCard
// ---------------------------------------------------------------------------

function StatusCard({ status }: { readonly status: LicenseStatus }) {
  if (!status.configured) {
    if (status.access_tier === 'expired') return <StatusCardNoLicenseExpired />;
    return <StatusCardCommunity status={status} />;
  }
  if (status.expired) return <StatusCardExpired status={status} />;
  if (status.valid && status.expires_at) {
    const days = daysUntil(status.expires_at);
    if (days <= 30) return <StatusCardActiveExpiring status={status} days={days} />;
    return <StatusCardActiveWithExpiry status={status} days={days} />;
  }
  if (status.valid) return <StatusCardActiveNoExpiry status={status} />;
  return <StatusCardInvalid status={status} />;
}

// ---------------------------------------------------------------------------
// SeatUsageBanner — T619: warning at 100% of max_seats, block notice at 110%.
// Renders nothing for Community (max_seats 0) or when comfortably under the
// warning threshold — this is a soft-enforcement signal, not a persistent
// status card like StatusCard above.
// ---------------------------------------------------------------------------

function SeatUsageBanner({ seats }: { readonly seats: LicenseSeats }) {
  if (!seats || seats.max_seats <= 0 || seats.state === 'ok') return null;

  const blocked = seats.state === 'blocked';
  return (
    <div
      role="alert"
      className={`orb-card p-4 mb-6 flex items-center gap-3 ${
        blocked ? 'border-danger/40 bg-danger/5' : 'border-warning/40 bg-warning/5'
      }`}
    >
      <Users className={`h-5 w-5 shrink-0 ${blocked ? 'text-danger' : 'text-warning'}`} aria-hidden="true" />
      <p className="text-sm text-fg-muted">
        <span className={`font-semibold ${blocked ? 'text-danger' : 'text-warning'}`}>
          {seats.active_users} of {seats.max_seats} seats active
        </span>{' '}
        {blocked
          ? '— new invitations are blocked until usage drops or the seat count is raised. Existing users are unaffected.'
          : '— you are at your licensed seat limit. New invitations will be blocked past 110% usage.'}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FeatureTable — when a license is present (valid or expired)
// ---------------------------------------------------------------------------

function FeatureTable({ features }: { readonly features: string[] }) {
  const enabledSet = new Set(features);
  return (
    <section className="orb-card mb-6 overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center gap-2">
        <Shield className="h-4 w-4 text-accent" aria-hidden="true" />
        <h2 className="orb-section-title text-sm">Feature flags</h2>
      </div>
      <Table bare>
        <THead>
          <TR>
            <TH>Feature</TH>
            <TH>Description</TH>
            <TH align="right">Status</TH>
          </TR>
        </THead>
        <TBody>
          {KNOWN_FEATURES.map((f) => {
            const enabled = enabledSet.has(f.id);
            return (
              <TR key={f.id}>
                <TD primary>{f.label}</TD>
                <TD>{f.description}</TD>
                <TD align="right">
                  {enabled ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-success">
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                      Enabled
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-fg-subtle">
                      <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                      Not included
                    </span>
                  )}
                </TD>
              </TR>
            );
          })}
        </TBody>
      </Table>
    </section>
  );
}

// ---------------------------------------------------------------------------
// CommunityFeatureTable — when no license is present
// ---------------------------------------------------------------------------

function CommunityFeatureTable() {
  return (
    <section className="orb-card mb-6 overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center gap-2">
        <Shield className="h-4 w-4 text-accent" aria-hidden="true" />
        <h2 className="orb-section-title text-sm">Feature flags</h2>
        <span className="ml-auto text-xs text-fg-muted">Community tier — no license required</span>
      </div>
      <Table bare>
        <THead>
          <TR>
            <TH>Feature</TH>
            <TH>Description</TH>
            <TH align="right">Status</TH>
          </TR>
        </THead>
        <TBody>
          {KNOWN_FEATURES.map((f) => (
            <TR key={f.id}>
              <TD className="font-medium">{f.label}</TD>
              <TD>{f.description}</TD>
              <TD align="right">
                <span className="inline-flex items-center gap-1 text-xs text-fg-subtle">
                  <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                  Requires license
                </span>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </section>
  );
}

// ---------------------------------------------------------------------------
// EnforcementTable — the T614 inventory rendered live (T622). Shown in every
// license state (community, trial, expired, enterprise) so a buyer/operator
// can see exactly which features are gated, where, and what's still open —
// not just the enabled/not-included summary the tables above show.
// ---------------------------------------------------------------------------

function featureLabel(id: string): string {
  return KNOWN_FEATURES.find((f) => f.id === id)?.label ?? id;
}

function EnforcementStatusBadge({ status }: { readonly status: LicenseEnforcementRow['status'] }) {
  if (status === 'enforced') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-success">
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
        Enforced
      </span>
    );
  }
  if (status === 'partial') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-warning">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
        Partial
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-fg-subtle">
      <Minus className="h-3.5 w-3.5" aria-hidden="true" />
      Not enforced (deliberate)
    </span>
  );
}

function EnforcementTable({ rows }: { readonly rows: LicenseEnforcementRow[] }) {
  if (!rows || rows.length === 0) return null;
  return (
    <section className="orb-card mb-6 overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-accent" aria-hidden="true" />
          <h2 className="orb-section-title text-sm">License enforcement</h2>
        </div>
        <p className="text-xs text-fg-muted mt-1">
          What each tier actually gates today, and where — the honest inventory behind the
          feature list above. Usage metering per feature isn't wired up yet.
        </p>
      </div>
      <Table bare>
        <THead>
          <TR>
            <TH>Feature</TH>
            <TH>Licensed</TH>
            <TH>Enforcement</TH>
            <TH>Where / gap</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map((row) => (
            <TR key={row.feature} className="align-top">
              <TD primary className="whitespace-nowrap align-top">{featureLabel(row.feature)}</TD>
              <TD className="align-top">
                {row.licensed ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-success">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Yes
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-fg-subtle">
                    <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                    No
                  </span>
                )}
              </TD>
              <TD className="align-top"><EnforcementStatusBadge status={row.status} /></TD>
              <TD className="text-xs max-w-md align-top">
                {row.points && row.points.length > 0 ? (
                  <p className="mb-1">
                    <span className="font-medium text-fg">Enforced at:</span> {row.points.join('; ')}
                  </p>
                ) : null}
                {row.gap ? (
                  <p>
                    <span className="font-medium text-warning">Gap:</span> {row.gap}
                    {row.closes_in ? ` (closes in ${row.closes_in})` : ''}
                  </p>
                ) : null}
                {!row.points?.length && !row.gap ? <span className="text-fg-subtle">—</span> : null}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </section>
  );
}

// ---------------------------------------------------------------------------
// TokenUploadForm
// ---------------------------------------------------------------------------

interface TokenUploadFormProps {
  readonly onUploaded: (updated: LicenseStatus) => void;
}

function TokenUploadForm({ onUploaded }: TokenUploadFormProps) {
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const t = token.trim();
    if (!t || saving) return;
    setSaving(true);
    setErr(null);
    setSaved(false);

    try {
      const updated = await adminLicenseApi.upload(t);
      onUploaded(updated);
      setSaved(true);
      setToken('');
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      let msg: string;
      if (e instanceof ApiError) {
        msg = `${e.code}: ${e.message}`;
      } else if (e instanceof Error) {
        msg = e.message;
      } else {
        msg = 'request failed';
      }
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="orb-card p-6">
      <div className="flex items-center gap-2 mb-4">
        <Key className="h-4 w-4 text-accent" aria-hidden="true" />
        <h2 className="orb-section-title text-sm">Upload license token</h2>
      </div>
      <p className="text-sm text-fg-muted mb-4">
        Paste the license token provided by Aphelion Holding. The server verifies
        the signature before storing it — the raw private key never leaves your
        infrastructure.
      </p>

      <form onSubmit={handleSubmit} aria-label="Upload license token">
        <div className="flex flex-col gap-1.5 mb-4">
          <label htmlFor="license-token" className="orb-section-title text-[11px]">
            License token
          </label>
          <textarea
            id="license-token"
            rows={4}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Paste your license token here…"
            className="
              w-full px-3 py-2 rounded-md border border-border
              bg-bg-inset text-fg text-xs font-mono placeholder-fg-subtle
              focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
              transition-colors resize-none
            "
            aria-describedby={err ? 'lic-err' : undefined}
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            {err ? (
              <p id="lic-err" role="alert" className="text-xs text-danger">{err}</p>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            {saved ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-success">
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
                License applied
              </span>
            ) : null}
            <button
              type="submit"
              disabled={!token.trim() || saving}
              className="
                inline-flex items-center gap-1.5 px-4 py-2 rounded-md
                text-xs font-semibold uppercase tracking-[0.08em]
                bg-accent text-accent-fg hover:opacity-90
                border border-accent/40 transition-opacity
                disabled:opacity-30 disabled:cursor-not-allowed
              "
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : null}
              {saving ? 'Verifying…' : 'Apply token'}
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Error banner
// ---------------------------------------------------------------------------

function ErrorBanner({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'Unknown error';
  }
  const hint =
    error instanceof ApiError && error.code === 'insufficient_role'
      ? 'Only org admins can manage the license.'
      : null;
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </div>
  );
}
